/**
 * Centralised DOMPurify wrapper — defensive XSS sanitization for the render path.
 *
 * WHY: backend writers call sanitize_rich_html (see /app/backend/html_sanitize.py)
 * before any HTML field lands in the database. This module re-sanitises the
 * same HTML at render time so that:
 *   1. legacy rows that pre-date the backend hardening cannot poison the DOM;
 *   2. a hostile third-party feed, AI rewriter, or inbound email that slipped
 *      past a newer/older backend path still gets stripped before hitting a
 *      `dangerouslySetInnerHTML` or iframe `srcDoc` sink;
 *   3. forgotten or net-new sinks can adopt `<SafeHtml>` / `safeHtml()` as
 *      the one-liner fix.
 *
 * Additionally — whenever DOMPurify strips something dangerous at render
 * time, we emit a synthetic report to /api/csp-report so the Admin Security
 * Console (`/admin/security`) surfaces every bypass attempt alongside
 * browser-emitted CSP reports. See BYPASS_REPORTER below.
 */
import React from "react";
import DOMPurify from "dompurify";

// A shared, configured instance. Hooks run on every call.
const purify = DOMPurify;

// ── Bypass reporter ─────────────────────────────────────────────────────────
// After DOMPurify runs, `purify.removed` lists every element/attribute that
// was stripped. We post a synthetic CSP-style event to /api/csp-report so the
// Security Console shows sanitisation events. Reports are debounced per
// session (one POST per 30s) and capped so a hostile preview can't DoS the
// endpoint. Tests + SSR skip reporting (no window/fetch).
let _lastReportAt = 0;
const _REPORT_INTERVAL_MS = 30_000;

function reportBypassAttempt(original, kinds) {
  if (typeof window === "undefined" || typeof fetch !== "function") return;
  const now = Date.now();
  if (now - _lastReportAt < _REPORT_INTERVAL_MS) return;
  _lastReportAt = now;
  try {
    const base = (typeof process !== "undefined"
      && process.env
      && process.env.REACT_APP_BACKEND_URL) || "";
    const report = {
      "csp-report": {
        "document-uri": window.location?.href || "",
        "violated-directive": "x-dompurify-sanitizer",
        "effective-directive": "x-dompurify-sanitizer",
        "blocked-uri": "inline",
        "source-file": "frontend/src/lib/safeHtml.js",
        "disposition": "enforce",
        "sample": String(original || "").slice(0, 300),
        "removed-kinds": kinds.slice(0, 20),
      },
    };
    // Fire-and-forget — we never block render on reporting.
    fetch(`${base}/api/csp-report`, {
      method: "POST",
      headers: { "Content-Type": "application/csp-report" },
      body: JSON.stringify(report),
      keepalive: true,
      credentials: "include",
    }).catch(() => {});
  } catch {
    /* noop — reporting must never break the page. */
  }
}

// Force target=_blank anchors to be rel="noopener noreferrer".
purify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("target")) {
    const existing = (node.getAttribute("rel") || "").toLowerCase();
    const parts = new Set(existing.split(/\s+/).filter(Boolean));
    parts.add("noopener");
    parts.add("noreferrer");
    node.setAttribute("rel", Array.from(parts).join(" "));
  }
});

const RICH_CONFIG = {
  // Mirror of backend _RICH_TAGS.
  ALLOWED_TAGS: [
    "p", "br", "span", "div",
    "strong", "b", "em", "i", "u", "s", "strike", "mark", "sub", "sup", "small",
    "ul", "ol", "li",
    "blockquote", "pre", "code",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "a", "img",
    "table", "thead", "tbody", "tfoot", "tr", "th", "td", "col", "colgroup",
    "hr",
  ],
  // Intentionally NOT setting ALLOWED_ATTR so DOMPurify's built-in safe
  // attribute allowlist (which already covers href/src/alt/title/class/
  // style/target/colspan/rowspan/etc.) applies. FORBID_ATTR below layers
  // our explicit event-handler denylist on top.
  // Only allow http/https/mailto/tel in URLs; blocks javascript:, vbscript:,
  // data:text/html, file:, about:, etc.
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#|\/)/i,
  // Explicitly re-add `target` because DOMPurify's default html profile
  // drops it for safety; we pair it with the afterSanitizeAttributes hook
  // above so every surviving target also carries noopener/noreferrer.
  ADD_ATTR: ["target"],
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form",
                "svg", "math", "link", "meta", "base"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus",
                "onblur", "onchange", "onsubmit", "onkeydown", "onkeyup",
                "onkeypress", "oninput", "onanimationstart", "onanimationend",
                "ontransitionend", "formaction", "xlink:href"],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: true,
  SAFE_FOR_TEMPLATES: false,
  RETURN_TRUSTED_TYPE: false,
  USE_PROFILES: { html: true },
};

const COMMENT_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "ul", "ol", "li", "a",
                 "blockquote", "mark"],
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|#|\/)/i,
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form",
                "svg", "math", "link", "meta", "base", "img"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus",
                "onblur", "formaction", "xlink:href", "style"],
  ALLOW_DATA_ATTR: false,
};

/** Rich-text sanitiser — use for templates, campaigns, specs, content blocks. */
export function safeHtml(input) {
  if (input == null) return "";
  const s = typeof input === "string" ? input : String(input);
  if (!s) return "";
  const out = purify.sanitize(s, RICH_CONFIG);
  // DOMPurify populates `removed` array AFTER each call with anything it
  // stripped. If the input contained a script, a dangerous URL, or an
  // event handler, `removed` will have entries — report it once.
  const removed = purify.removed || [];
  if (removed.length > 0) {
    const kinds = removed.map(r => {
      if (r.element) return (r.element.tagName || r.element.nodeName || "el").toLowerCase();
      if (r.attribute) return `@${r.attribute.name}`;
      return "?";
    });
    reportBypassAttempt(s, kinds);
  }
  return out;
}

/** Tight sanitiser for CRM notes / meeting highlights / short snippets. */
export function safeCommentHtml(input) {
  if (input == null) return "";
  const s = typeof input === "string" ? input : String(input);
  if (!s) return "";
  const out = purify.sanitize(s, COMMENT_CONFIG);
  const removed = purify.removed || [];
  if (removed.length > 0) {
    const kinds = removed.map(r => {
      if (r.element) return (r.element.tagName || r.element.nodeName || "el").toLowerCase();
      if (r.attribute) return `@${r.attribute.name}`;
      return "?";
    });
    reportBypassAttempt(s, kinds);
  }
  return out;
}

/**
 * Build a srcDoc for a preview iframe. The iframe MUST still be rendered with
 * sandbox="" (empty string — no allow-scripts, no allow-same-origin) so that
 * even if a novel bypass ever made it past DOMPurify, the rendered document
 * runs with null origin and no script execution.
 */
export function safeSrcDoc(input) {
  return safeHtml(input);
}

/**
 * Shared React wrapper for any site that would otherwise call
 * `dangerouslySetInnerHTML` directly. Every caller goes through DOMPurify
 * exactly once; the single eslint-disable lives here so the project-wide
 * `no-restricted-syntax` rule keeps protecting every other file.
 *
 * Props:
 *   html:   user-authored / DB-persisted HTML string
 *   tight:  when true, uses the comment-grade allowlist (drops img/table)
 *   as:     tag name to render ("div" by default)
 *   ...rest: standard props (className, style, data-testid, etc.)
 */
export function SafeHtml({ html, tight = false, as: Tag = "div", ...rest }) {
  const cleaned = tight ? safeCommentHtml(html) : safeHtml(html);
  // The one and only sanctioned sink. Every other usage in the app is
  // flagged by .eslintrc no-restricted-syntax so we cannot regress.
  // eslint-disable-next-line no-restricted-syntax
  return <Tag {...rest} dangerouslySetInnerHTML={{ __html: cleaned }} />;
}
